import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import { SupportContextBanner } from "@/features/staff/components/SupportContextBanner";
import { StaffOnly } from "@/features/staff/components/StaffOnly";
import {
  useCloseSupportContext,
  useSupportAccount,
  useSupportApps,
  useSupportContext,
  useSupportDeployments,
} from "@/features/staff/hooks/useStaff";
import { formatDate } from "@/lib/formatters";

export const Route = createFileRoute("/_authenticated/staff/$accountId")({
  component: StaffAccountPage,
});

function StaffAccountPage() {
  return (
    <StaffOnly>
      <StaffAccountView />
    </StaffOnly>
  );
}

function StaffAccountView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams({ from: "/_authenticated/staff/$accountId" });
  const { context, clear, remainingMs, expired } = useSupportContext();
  const closeContext = useCloseSupportContext();
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

  const contextId = context && context.targetAccountId === accountId ? context.id : null;
  const account = useSupportAccount(accountId, contextId);
  const apps = useSupportApps(accountId, contextId);
  const deployments = useSupportDeployments(selectedAppId, contextId);

  const refused = account.error?.status === 404 || apps.error?.status === 404;
  useEffect(() => {
    if (refused) clear();
  }, [refused, clear]);

  const endSupport = () => {
    if (!context) {
      clear();
      void navigate({ to: "/staff" });
      return;
    }
    closeContext.mutate(context.id, {
      onSuccess: () => {
        clear();
        void navigate({ to: "/staff" });
      },
    });
  };

  if (!contextId) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-border bg-surface px-6 py-8 text-center">
        <h1 className="text-lg font-semibold text-heading">{t("staff.noContext.title")}</h1>
        <p className="mt-2 text-sm text-muted">{t("staff.noContext.body")}</p>
        <button
          type="button"
          onClick={() => void navigate({ to: "/staff" })}
          className="mt-4 rounded-lg bg-indigo px-4 py-2 text-sm font-medium text-white hover:bg-indigo-bright"
        >
          {t("staff.noContext.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="sr-only">
        {expired
          ? t("staff.banner.expired")
          : t("staff.banner.active", { target: account.data?.account.email ?? accountId })}
      </h1>
      <SupportContextBanner
        targetLabel={account.data?.account.email ?? accountId}
        remainingMs={remainingMs}
        expired={expired}
        onClose={endSupport}
        closing={closeContext.isPending}
      />

      {closeContext.error && <ApiErrorBanner error={closeContext.error} className="mb-4" />}

      {account.isLoading && <PageLoader />}
      {account.error && <ApiErrorBanner error={account.error} className="mt-4" />}

      {account.data && (
        <section className="rounded-lg border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold text-heading">{t("staff.account.title")}</h2>
          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("staff.account.email")} value={account.data.account.email} />
            <Field label={t("staff.account.name")} value={account.data.account.name || "—"} />
            <Field
              label={t("staff.account.mfa")}
              value={account.data.account.mfaEnabled ? t("staff.yes") : t("staff.no")}
            />
            <Field
              label={t("staff.account.verified")}
              value={account.data.account.emailVerified ? t("staff.yes") : t("staff.no")}
            />
            <Field
              label={t("staff.account.created")}
              value={formatDate(account.data.account.createdAt)}
            />
            <Field
              label={t("staff.account.deletion")}
              value={
                account.data.account.deletionScheduledFor
                  ? formatDate(account.data.account.deletionScheduledFor)
                  : "—"
              }
            />
          </dl>

          {account.data.tenant && (
            <>
              <h3 className="mt-6 text-sm font-semibold text-heading">{t("staff.tenant.title")}</h3>
              <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t("staff.tenant.plan")} value={account.data.tenant.planTier} />
                <Field
                  label={t("staff.tenant.status")}
                  value={account.data.tenant.billingStatus ?? "—"}
                />
                <Field
                  label={t("staff.tenant.paymentMethod")}
                  value={account.data.tenant.hasPaymentMethod ? t("staff.yes") : t("staff.no")}
                />
                <Field
                  label={t("staff.tenant.madQuota")}
                  value={String(account.data.tenant.madQuota)}
                />
              </dl>
            </>
          )}

          {account.data.transfers.length > 0 && (
            <>
              <h3 className="mt-6 text-sm font-semibold text-heading">
                {t("staff.transfers.title")}
              </h3>
              <ul className="mt-3 space-y-2">
                {account.data.transfers.map((transfer) => (
                  <li
                    key={transfer.id}
                    className="rounded-lg border border-border px-3 py-2 text-sm text-body"
                  >
                    {t("staff.transfers.row", {
                      app: transfer.appName,
                      from: transfer.fromEmail,
                      to: transfer.toEmail,
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      <section className="mt-6 rounded-lg border border-border bg-surface p-5">
        <h2 className="text-lg font-semibold text-heading">{t("staff.apps.title")}</h2>
        {apps.isLoading && <PageLoader />}
        {apps.error && <ApiErrorBanner error={apps.error} className="mt-3" />}
        {apps.data && apps.data.apps.length === 0 && (
          <p className="mt-3 text-sm text-muted">{t("staff.apps.empty")}</p>
        )}
        {apps.data && apps.data.apps.length > 0 && (
          <ul className="mt-3 space-y-2">
            {apps.data.apps.map((app) => (
              <li
                key={app.appId}
                className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-heading">{app.name}</p>
                  <p className="text-xs text-muted">
                    {t("staff.apps.row", {
                      role: app.role ?? "—",
                      collaborators: app.collaboratorCount,
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedAppId(app.appId)}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted hover:bg-elevated hover:text-heading"
                >
                  {t("staff.apps.deployments")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedAppId && (
        <section className="mt-6 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold text-heading">{t("staff.deployments.title")}</h2>
          {deployments.isLoading && <PageLoader />}
          {deployments.error && <ApiErrorBanner error={deployments.error} className="mt-3" />}
          {deployments.data && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-subtle">
                  <tr>
                    <th className="py-2 pr-4">{t("staff.deployments.name")}</th>
                    <th className="py-2 pr-4">{t("staff.deployments.keyPrefix")}</th>
                    <th className="py-2 pr-4">{t("staff.deployments.latest")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.data.deployments.map((deployment) => (
                    <tr key={deployment.deploymentId} className="border-t border-border">
                      <td className="py-2 pr-4 text-body">{deployment.name}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-muted">
                        {deployment.keyPrefix}…
                      </td>
                      <td className="py-2 pr-4 text-body">{deployment.latestLabel ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase text-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-body">{value}</dd>
    </div>
  );
}
